import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, Warning, ArrowsClockwise, MagnifyingGlass,
  Play, ListChecks, FileText, FolderSimple,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import MoveRecordingModal from "../components/recordings/MoveRecordingModal";
import { getRecording, getRecordingAudioUrl } from "../api/recordings";
import { getMsStatus } from "../api/microsoft";

const ONEDRIVE_DISABLED_TOOLTIP = "Connect OneDrive to enable this action";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function StatusPill({ status }) {
  if (status === "completed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="detail-status-completed">
        <CheckCircle size={10} weight="fill" /> Completed
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="detail-status-failed">
        <Warning size={10} weight="fill" /> Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full uppercase tracking-wide" data-testid="detail-status-processing">
      <ArrowsClockwise size={10} className="animate-spin" /> Processing
    </span>
  );
}

export default function RecordingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [onedriveConnected, setOnedriveConnected] = useState(false);
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    getMsStatus()
      .then(({ connected }) => setOnedriveConnected(!!connected))
      .catch(() => setOnedriveConnected(false));
  }, []);

  const fetchDetail = useCallback(async () => {
    try {
      const data = await getRecording(id);
      setRecording(data);
      return data;
    } catch (e) {
      toast.error("Could not load this recording.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Poll while processing so the page updates itself once Gemini finishes.
  useEffect(() => {
    if (recording?.status === "processing" && !pollRef.current) {
      pollRef.current = setInterval(fetchDetail, 5000);
    } else if (recording?.status !== "processing" && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [recording?.status, fetchDetail]);

  const seekTo = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      audioRef.current.play().catch(() => {});
    }
  };

  const segments = recording?.transcript_segments || [];
  const filteredSegments = search.trim()
    ? segments.filter((s) => (s.text || "").toLowerCase().includes(search.trim().toLowerCase()))
    : segments;

  if (loading) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center bg-slate-50" data-testid="recording-detail-loading">
        <ArrowsClockwise size={24} className="text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!recording) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center bg-slate-50 gap-3" data-testid="recording-detail-not-found">
        <p className="text-sm text-slate-500">Recording not found.</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/recordings")}>Back to Recordings</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden bg-slate-50" data-testid="recording-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-4 flex-shrink-0">
        <button
          onClick={() => navigate("/recordings")}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-violet-600 transition-colors mb-3"
          data-testid="back-to-recordings-btn"
        >
          <ArrowLeft size={13} /> Back to Recordings
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-600 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full" data-testid="recording-project-badge">
                {recording.project_name || "Untitled Project"}
              </span>
              <StatusPill status={recording.status} />
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900">Meeting Recording</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {formatDate(recording.created_at)} &middot; {formatDuration(recording.duration)}
            </p>
          </div>
          <span title={!onedriveConnected ? ONEDRIVE_DISABLED_TOOLTIP : undefined}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { if (onedriveConnected) setMoveModalOpen(true); }}
              disabled={!onedriveConnected}
              className={!onedriveConnected ? "cursor-not-allowed" : ""}
              data-testid="detail-move-to-folder-btn"
            >
              <FolderSimple size={14} className="mr-1.5" /> Move to Folder
            </Button>
          </span>
        </div>

        {recording.status !== "processing" && (
          <audio
            ref={audioRef}
            controls
            src={getRecordingAudioUrl(id)}
            className="w-full mt-4"
            data-testid="recording-audio-player"
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        {recording.status === "processing" && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 bg-white border border-slate-200" data-testid="recording-processing-state">
            <ArrowsClockwise size={24} className="text-violet-500 animate-spin" />
            <p className="text-sm text-slate-600 font-medium">Gemini is transcribing this recording...</p>
            <p className="text-xs text-slate-400">This page updates automatically once it&apos;s done.</p>
          </div>
        )}

        {recording.status === "failed" && (
          <div className="bg-red-50 border border-red-200 px-4 py-4 mb-6" data-testid="recording-failed-state">
            <p className="text-sm font-semibold text-red-700 mb-1">Processing failed</p>
            <p className="text-xs text-red-600">{recording.error || "An unknown error occurred."}</p>
          </div>
        )}

        {recording.status === "completed" && (
          <Tabs defaultValue="summary" className="w-full">
            <TabsList data-testid="detail-tabs">
              <TabsTrigger value="summary" data-testid="tab-summary">
                <ListChecks size={14} className="mr-1.5" /> Summary &amp; Action Items
              </TabsTrigger>
              <TabsTrigger value="transcript" data-testid="tab-transcript">
                <FileText size={14} className="mr-1.5" /> Transcript
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary" data-testid="summary-tab-content">
              <div className="bg-white border border-slate-200 p-6 space-y-6">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Executive Summary</h3>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {recording.summary || "No summary available."}
                  </p>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Action Items</h3>
                  {recording.action_items?.length ? (
                    <ul className="space-y-2">
                      {recording.action_items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700" data-testid={`action-item-${idx}`}>
                          <CheckCircle size={15} className="text-violet-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400">No action items identified.</p>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="transcript" data-testid="transcript-tab-content">
              <div className="bg-white border border-slate-200">
                <div className="p-3 border-b border-slate-100">
                  <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search transcript..."
                      className="pl-8 h-9"
                      data-testid="transcript-search-input"
                    />
                  </div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
                  {filteredSegments.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-10">
                      {search ? "No matching transcript segments." : "No transcript available."}
                    </p>
                  ) : (
                    filteredSegments.map((seg, idx) => (
                      <button
                        key={idx}
                        onClick={() => seekTo(seg.timestamp_seconds)}
                        className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-violet-50 transition-colors text-left"
                        data-testid={`transcript-segment-${idx}`}
                      >
                        <span className="flex items-center gap-1 text-xs font-mono text-violet-600 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5 flex-shrink-0 mt-0.5">
                          <Play size={9} weight="fill" /> {formatTimestamp(seg.timestamp_seconds)}
                        </span>
                        <span className="text-sm text-slate-700 leading-relaxed">
                          {seg.speaker && <span className="font-semibold text-slate-500 mr-1">{seg.speaker}:</span>}
                          {seg.text}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {moveModalOpen && (
        <MoveRecordingModal
          recording={recording}
          onClose={() => setMoveModalOpen(false)}
          onMoved={(updated) => setRecording((prev) => ({ ...prev, folder_id: updated.folder_id }))}
        />
      )}
    </div>
  );
}
